/** @jsx jsx */
// =============================================================
// jobs.js – Jobs-Übersichtsseite (neues Design laut XD, Ticket 20022)
//
// Pixelgenau nach Adobe-XD "Web 1920 – 31" (Florian / KR8):
//   Schriftskala 120px/110 – 50px/70 bzw. /60 – 18px/25 (Telegraf)
//   Sektionsnummern 120px in #B2B2B2, Spaltenraster x45/93/651/1200
//   Alle Größen skalieren proportional zur Containerbreite (cqw),
//   Referenzbreite 1920px -> 1px Design = (px/19.2)cqw.
//
// Inhaltsverzeichnis:
//   1. Imports & Setup
//   2. Skalierungs-Helfer & Keyframes
//   3. Styled Components
//   4. Hauptkomponente caly_JobsSeite
//   5. GraphQL-Abfrage & Export
// =============================================================

// ─────────────────────────────────────────────────────────────
// 1. Imports & Setup
// ─────────────────────────────────────────────────────────────
import React from 'react'
import { jsx, keyframes } from '@emotion/core'
import styled from '@emotion/styled'
import { graphql, useStaticQuery, Link } from 'gatsby'
import Img from 'gatsby-image'
import Layout from '../components/Layout'
import Container from '../components/Container'
import SEO from '../components/SEO'
import Newsletter from '../components/NewsletterPopup'
import Menu from '../components/Menu'
import MenuBottom from '../components/MenuBottom'
import Caly_TeamSlider from '../components/caly_TeamSlider'
import caly_kulturDaten from '../components/caly_KulturDaten'

// ─────────────────────────────────────────────────────────────
// 2. Skalierungs-Helfer & Keyframes
// ─────────────────────────────────────────────────────────────

// Rechnet Design-Pixel (1920er-Artboard) in Container-Prozent um.
// min = Untergrenze, damit Text am Handy lesbar bleibt.
function caly_px(designPx, min) {
  const caly_cqw = (designPx / 19.2).toFixed(3)
  return `clamp(${min}px, ${caly_cqw}cqw, ${designPx}px)`
}

// Endlos-Scroll von rechts nach links (-50% weil Inhalt doppelt)
const caly_marqueeAnimation = keyframes`
  0%   { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`

// ─────────────────────────────────────────────────────────────
// 3. Styled Components
// ─────────────────────────────────────────────────────────────

// Seiten-Wrapper: definiert den Container für die cqw-Skalierung
const Caly_Seite = styled.div`
  width: 100%;
  container-type: inline-size;
  position: relative;

  /* Gleicher Seitenrahmen wie der Header (ab "large"), damit die
     Menüspalte exakt mit der Social-Spalte des Headers fluchtet */
  @media (min-width: ${props => props.theme.responsive.large}) {
    border-left: 2px solid #000;
    border-right: 2px solid #000;
  }
`

// Hauptmenü als rechte Spalte neben dem Hero-Foto (wie im XD):
// nutzt die bestehenden Menu/MenuBottom-Komponenten der Seite.
// Die Spalte liegt IM Fluss und wird per Flex-Stretch exakt gleich
// hoch wie das Foto (die Menüpunkte verteilen die Höhe über flex:1).
const Caly_MenuSpalte = styled.div`
  flex: 0 0 280px;
  width: 280px;
  display: flex;
  flex-direction: column;

  nav li {
    background: #fff;
  }

  nav li.dark {
    background: #000;
  }

  @media (max-width: ${props => props.theme.responsive.mediumMax}) {
    display: none;
  }
`

// ── HERO ─────────────────────────────────────────────────────

// Hero-Zeile: Foto links, Menü rechts – beide exakt gleich hoch.
// Die Zeile hat eine FESTE Höhe (Design: 684px, skaliert), damit
// nicht das Seitenverhältnis des Fotos die Höhe bestimmt. Das
// max() sichert die Mindesthöhe, die das Menü inhaltlich braucht.
const Caly_HeroZeile = styled.div`
  display: flex;
  align-items: stretch;
  width: 100%;

  @media (min-width: ${props => props.theme.responsive.medium}) {
    height: max(${caly_px(684, 220)}, 520px);
  }
`

// Bild-Box im Hero-Bereich (Design: 1920×684) – das Foto liegt
// NEBEN dem Menü und füllt die gemeinsame Zeilenhöhe komplett aus
// (Überstand wird durch objectFit: cover beschnitten)
const Caly_HeroFotoBox = styled.div`
  flex: 1;
  min-width: 0;

  .gatsby-image-wrapper {
    height: ${caly_px(684, 220)};
  }

  @media (min-width: ${props => props.theme.responsive.medium}) {
    /* Trennlinie zum Menü – wie im Header (Linie gehört der
       linken Spalte, damit sie mit der Social-Spalte fluchtet) */
    border-right: 2px solid #000;

    .gatsby-image-wrapper {
      height: 100%;
    }
  }
`

// Titelzeile unter dem Foto (Titel links, Stern + Text rechts)
const Caly_HeroUnten = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: ${caly_px(60, 24)} 0 ${caly_px(60, 24)} ${caly_px(45, 15)};

  @media (min-width: ${props => props.theme.responsive.medium}) {
    flex-direction: row;
    justify-content: space-between;
  }
`

// Haupt-Überschrift "STARTE DEINE KARRIERE BEI UNS"
// Design: 120px/110px, x45, Breite 863
const Caly_HeroH1 = styled.h1`
  font-size: ${caly_px(120, 40)};
  line-height: 0.917;
  text-transform: uppercase;
  margin: 0;
  max-width: 12ch;
`

// Rechte Hälfte: Stern + Beschreibungstext (Design: x1210, Breite 520)
const Caly_HeroDescBox = styled.div`
  flex: 0 0 auto;
  padding-top: ${caly_px(30, 12)};

  @media (min-width: ${props => props.theme.responsive.medium}) {
    width: ${caly_px(520, 240)};
    margin-right: ${caly_px(190, 15)};
    padding-top: 0;
  }
`

// Doppelstern (Design: 95×97, auf Höhe der Titel-Oberkante)
const Caly_Stern = styled.div`
  width: ${caly_px(95, 34)};
  height: ${caly_px(97, 35)};
  margin-bottom: ${caly_px(70, 20)};

  svg {
    width: 100%;
    height: 100%;
    display: block;
    fill: #000;
  }
`

// Beschreibungstext (Design: 18px/25px)
const Caly_HeroDesc = styled.p`
  font-size: ${caly_px(18, 13)};
  line-height: 1.389;
  margin: 0;
`

// ── MARQUEE ──────────────────────────────────────────────────

// Grüner Streifen (Design: 1920×154, KEINE Rahmenlinien)
const Caly_MarqueeWrapper = styled.div`
  width: 100%;
  background: ${props => props.theme.colors.green};
  overflow: hidden;
  height: ${caly_px(154, 56)};
  display: flex;
  align-items: center;
`

// Animierter Inhalts-Container (doppelt für seamless loop)
const Caly_MarqueeLauf = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  width: max-content;
  animation: ${caly_marqueeAnimation} 25s linear infinite;
`

// Pill-Tag im Marquee (Design: 349×70, weiß gefüllt, Radius 35)
const Caly_MarqueePill = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  text-transform: uppercase;
  font-size: ${caly_px(18, 12)};
  letter-spacing: 0.02em;
  height: ${caly_px(70, 28)};
  padding: 0 ${caly_px(60, 20)};
  margin: 0 ${caly_px(15, 6)};
  border: 2px solid #000;
  border-radius: 999px;
  color: #000;
  background: #fff;
  text-decoration: none;
  opacity: 1 !important;

  &:hover {
    background: #000;
    color: #fff;
  }
`

// ── NUMMERIERTE ABSCHNITTE ────────────────────────────────────

// Sektions-Kopf: Nummer (grau, x69) + Titel (schwarz, x651)
const Caly_SektionRow = styled.div`
  position: relative;
  width: 100%;
  padding: ${caly_px(76, 30)} 0 ${caly_px(70, 28)} 0;
`

// Graue Nummer (Design: 120px/110, #B2B2B2, x69)
const Caly_SektionNummer = styled.div`
  position: absolute;
  top: ${caly_px(76, 30)};
  left: ${caly_px(69, 15)};
  font-size: ${caly_px(120, 40)};
  line-height: 0.917;
  color: #b2b2b2;
`

// Sektionstitel (Design: 120px/110, x651)
const Caly_SektionTitel = styled.div`
  font-size: ${caly_px(120, 40)};
  line-height: 0.917;
  text-transform: uppercase;
  margin-left: ${caly_px(651, 120)};
  padding-right: ${caly_px(45, 15)};
`

// ── FOTOBAND ─────────────────────────────────────────────────

// Vollbreites Fotoband (Design: Höhe 424, keine Rahmenlinien)
const Caly_FotoBand = styled.div`
  width: 100%;
  overflow: hidden;
`

// Animierter Innen-Container (Inhalt doppelt für nahtlosen Loop)
const Caly_FotoBandLauf = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  width: max-content;
  animation: ${caly_marqueeAnimation} 60s linear infinite;

  img {
    display: block;
    height: ${caly_px(424, 160)};
    width: auto;
  }
`

// ── "KLEINES TEAM" TEXTBLOCK ─────────────────────────────────

// Zweispaltig: Titel x93 (Breite 507), Text x650 (Breite 520)
const Caly_TeamTextRow = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: ${caly_px(104, 40)} 0 0 ${caly_px(93, 15)};

  @media (min-width: ${props => props.theme.responsive.medium}) {
    flex-direction: row;
  }
`

// "Kleines Team, große Chancen" (Design: 50px/60)
const Caly_TeamTextLinks = styled.div`
  font-size: ${caly_px(50, 22)};
  line-height: 1.2;
  width: ${caly_px(507, 200)};
  flex: 0 0 auto;
  margin-bottom: ${caly_px(30, 16)};
`

// Fließtext rechts (Design: 18px/25, x650, Breite 520)
const Caly_TeamTextRechts = styled.div`
  font-size: ${caly_px(18, 13)};
  line-height: 1.389;

  @media (min-width: ${props => props.theme.responsive.medium}) {
    width: ${caly_px(520, 240)};
    margin-left: ${caly_px(50, 10)};
  }
`

// ── JOB-LISTE ────────────────────────────────────────────────

// "WIR SUCHEN AKTUELL:" (Design: 120px/110, x651)
const Caly_JobsHeader = styled.div`
  width: 100%;
  font-size: ${caly_px(120, 40)};
  line-height: 0.917;
  text-transform: uppercase;
  padding: ${caly_px(118, 40)} ${caly_px(45, 15)} ${caly_px(100, 36)}
    ${caly_px(651, 120)};
`

// Container für alle Job-Zeilen – EIN durchgehender Verlauf
// (Design: #00FF27 -> #FCFFFC bei 96% -> #FFF, Rahmen oben/unten)
const Caly_JobsContainer = styled.div`
  width: 100%;
  border-top: 2px solid #000;
  border-bottom: 2px solid #000;
  background: transparent
    linear-gradient(90deg, #00ff27 0%, #fcfffc 96%, #ffffff 100%) 0% 0%
    no-repeat padding-box;
  padding: ${caly_px(45, 16)} 0;
`

// Einzelne Job-Zeile (Design: Zeilenabstand ~151px, keine Trennlinien)
const Caly_JobZeile = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  width: 100%;
  padding: ${caly_px(22, 8)} ${caly_px(49, 15)} ${caly_px(22, 8)}
    ${caly_px(93, 15)};
`

// Jobtitel (Design: 50px/60, x93, Breite 507)
const Caly_JobTitel = styled.div`
  flex: 1 1 auto;
  font-size: ${caly_px(50, 18)};
  line-height: 1.2;
  min-width: 140px;
`

// Ort-Angabe (Design: 50px, x898)
const Caly_JobOrt = styled.div`
  flex: 0 0 auto;
  font-size: ${caly_px(50, 16)};
  line-height: 1.2;

  @media (min-width: ${props => props.theme.responsive.medium}) {
    width: ${caly_px(303, 90)};
  }
`

// Arbeitszeit (Design: 50px, x1201) – rechts etwas Luft zum Button
const Caly_JobZeit = styled.div`
  flex: 0 0 auto;
  font-size: ${caly_px(50, 16)};
  line-height: 1.2;
  padding: 0 ${caly_px(20, 8)};

  @media (min-width: ${props => props.theme.responsive.medium}) {
    width: ${caly_px(260, 80)};
    padding: 0 ${caly_px(24, 10)} 0 0;
  }
`

// "mehr Infos"-Button (Design: 410×70, Radius 35, Text 50px)
const Caly_JobLink = styled(Link)`
  flex: 0 0 auto;
  opacity: 1 !important;
  margin-top: 8px;

  @media (min-width: ${props => props.theme.responsive.medium}) {
    margin-top: 0;
  }

  span {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    width: ${caly_px(410, 120)};
    height: ${caly_px(70, 34)};
    border: 2px solid #000;
    font-size: ${caly_px(50, 15)};
    line-height: 1;
    white-space: nowrap;
    color: #000;

    &:hover {
      background-color: #000;
      color: #fff;
    }
  }
`

// ── KULTUR-ICONS ─────────────────────────────────────────────

// Raster der 6 Icons: ZWEI Spalten (x651 und x1200), drei Reihen
const Caly_KulturGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${caly_px(90, 30)} 0;
  width: 100%;
  padding: ${caly_px(100, 36)} ${caly_px(45, 15)} ${caly_px(120, 40)}
    ${caly_px(651, 120)};

  @media (min-width: ${props => props.theme.responsive.small}) {
    grid-template-columns: ${caly_px(550, 200)} 1fr;
  }
`

// Einzelne Kultur-Karte (Icon + Text)
const Caly_KulturKarte = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${caly_px(16, 8)};
`

// Icon-Box (Design: 124×124)
const Caly_KulturIcon = styled.div`
  height: ${caly_px(124, 56)};
  display: flex;
  align-items: flex-end;

  img {
    display: block;
    max-height: 100%;
    width: auto;
  }
`

// Text zur Karte (Design: 18px/25, Breite 370)
const Caly_KulturText = styled.div`
  font-size: ${caly_px(18, 13)};
  line-height: 1.389;
  max-width: ${caly_px(370, 220)};
`

// Textblock unter den Icons (Design: Spalte x651, Breite 520)
const Caly_KulturTextZwei = styled.div`
  width: 100%;
  padding: ${caly_px(40, 20)} ${caly_px(45, 15)} ${caly_px(120, 40)}
    ${caly_px(651, 120)};
`

// Text-Spalte (Design: 18px/25)
const Caly_KulturTextSpalte = styled.div`
  font-size: ${caly_px(18, 13)};
  line-height: 1.389;
  max-width: ${caly_px(1070, 500)};
`

// ── ANSPRECHPARTNER ──────────────────────────────────────────

// Sektion: Foto links (x93, ~330px), Inhalt ab x651
const Caly_KontaktRow = styled.div`
  position: relative;
  width: 100%;
  padding: ${caly_px(40, 16)} 0 ${caly_px(120, 40)} 0;
`

// Rundes Foto (Design: ~330px Durchmesser, x93)
const Caly_KontaktFotoWrapper = styled.div`
  width: ${caly_px(330, 100)};
  height: ${caly_px(330, 100)};
  border-radius: 50%;
  overflow: hidden;
  border: 1px solid #000;
  margin: 0 0 ${caly_px(30, 14)} ${caly_px(93, 15)};

  .gatsby-image-wrapper {
    width: 100% !important;
    height: 100% !important;
  }

  @media (min-width: ${props => props.theme.responsive.medium}) {
    position: absolute;
    top: ${caly_px(60, 20)};
    left: ${caly_px(93, 15)};
    margin: 0;
  }
`

// Inhaltsspalte ab x651
const Caly_KontaktInhalt = styled.div`
  padding-left: ${caly_px(93, 15)};

  @media (min-width: ${props => props.theme.responsive.medium}) {
    padding-left: ${caly_px(651, 120)};
  }
`

// Name-Zeile mit Linie bis zum rechten Rand (Design: 50px + 1px Linie)
const Caly_KontaktName = styled.div`
  font-size: ${caly_px(50, 22)};
  line-height: 1.4;
  border-bottom: 1px solid #000;
  padding-bottom: ${caly_px(8, 4)};
`

// Rolle mit Linie (Design: 50px + 1px Linie)
const Caly_KontaktRolle = styled.div`
  font-size: ${caly_px(50, 22)};
  line-height: 1.4;
  border-bottom: 1px solid #000;
  padding-bottom: ${caly_px(8, 4)};
  margin-bottom: ${caly_px(50, 20)};
`

// Call-Zeile (Design: "Call" mit orangem Rahmen + Nummer 50px)
const Caly_CallZeile = styled.div`
  display: flex;
  align-items: center;
  gap: ${caly_px(30, 12)};
  margin-top: ${caly_px(50, 20)};
  font-size: ${caly_px(50, 18)};

  a {
    opacity: 1 !important;
    color: #000;
  }

  span.caly-call {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 2px solid ${props => props.theme.colors.red};
    padding: ${caly_px(4, 2)} ${caly_px(22, 10)};
  }
`

// E-Mail-Button (statt Kontaktformular – Kundenwunsch von Harry:
// ein Klick öffnet eine Mail direkt an ihn; Optik wie die eckigen
// Orange-Buttons des Designs, passend zur Call-Zeile darunter)
const Caly_MailBtn = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 2px solid ${props => props.theme.colors.red};
  background: transparent;
  color: #000;
  text-decoration: none;
  opacity: 1 !important;
  padding: ${caly_px(6, 3)} ${caly_px(40, 16)};
  font-size: ${caly_px(50, 18)};
  cursor: pointer;
  margin-top: ${caly_px(50, 20)};

  &:hover {
    background: ${props => props.theme.colors.red};
    color: #fff;
  }
`

// ─────────────────────────────────────────────────────────────
// 4. Hauptkomponente caly_JobsSeite
// ─────────────────────────────────────────────────────────────

// Wiederverwendbarer Doppelstern (nach XD-Vorlage, 95×97)
function Caly_SternIcon() {
  return (
    <svg viewBox="0 0 95 97" aria-hidden="true">
      {/* großer Vierzack-Stern */}
      <path d="M57 32 C60 55 68 63 91 66 C68 69 60 77 57 97 C54 77 46 69 23 66 C46 63 54 55 57 32 Z" />
      {/* kleiner Vierzack-Stern oben links */}
      <path d="M24 0 C26 14 31 19 45 21 C31 23 26 28 24 42 C22 28 17 23 3 21 C17 19 22 14 24 0 Z" />
    </svg>
  )
}

function caly_JobsSeite() {
  const caly_data = useStaticQuery(graphql`
    {
      # Jobs für Liste und Marquee
      allContentfulJobs(sort: { order: ASC, fields: publishDate }) {
        edges {
          node {
            contentful_id
            slug
            title
            ort
            arbeitszeit
          }
        }
      }

      # Hero-Bild (Original aus der XD-Vorlage: Mann mit Tablet)
      caly_heroFoto: file(relativePath: { eq: "extendit-jobs2-hero.jpg" }) {
        childImageSharp {
          fluid(maxWidth: 1920, quality: 85) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }

      # Fotoband (Sektion 01) – die neuen Fotos von Clemens Schneider
      caly_bandFotos: allFile(
        filter: { relativePath: { regex: "/extendit-jobs2-team/" } }
        sort: { fields: relativePath, order: ASC }
      ) {
        edges {
          node {
            relativePath
            childImageSharp {
              fixed(height: 424, quality: 82) {
                src
                srcSet
              }
            }
          }
        }
      }

      # Ansprechpartner-Foto laut XD-Design: Harald Pfeiffer
      # (lokales Asset aus dem Clemens-Schneider-Shooting)
      caly_haraldFoto: file(
        relativePath: { eq: "extendit-harald-jobseite.jpg" }
      ) {
        childImageSharp {
          fluid(maxWidth: 660, maxHeight: 660, quality: 85) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
    }
  `)

  const caly_jobs = caly_data.allContentfulJobs.edges
  const caly_heroFoto = caly_data.caly_heroFoto.childImageSharp.fluid
  const caly_bandFotos = caly_data.caly_bandFotos.edges

  // Marquee-Basis: Jobs so oft wiederholen, dass der grüne Streifen
  // immer voll ist (mindestens 8 Pills, wird für den Loop verdoppelt)
  const caly_marqueeBasis = []
  while (caly_marqueeBasis.length < 8 && caly_jobs.length > 0) {
    caly_marqueeBasis.push(...caly_jobs)
  }

  // Ansprechpartner-Foto (lokales Asset)
  const caly_haraldFoto = caly_data.caly_haraldFoto
    ? caly_data.caly_haraldFoto.childImageSharp.fluid
    : null

  return (
    <Layout>
      <Newsletter />
      <SEO
        title="Jobs"
        description="Offene Stellen bei EXTENDIT – starte deine Karriere bei uns."
      />

      <Container>
        <Caly_Seite>
          {/* ── HERO: Foto links, Hauptmenü rechts (gleich hoch) ── */}
          <Caly_HeroZeile>
            <Caly_HeroFotoBox>
              <Img
                fluid={caly_heroFoto}
                alt="Karriere bei EXTENDIT"
                imgStyle={{ objectFit: 'cover', objectPosition: 'center 30%' }}
              />
            </Caly_HeroFotoBox>
            <Caly_MenuSpalte>
              <Menu bottommenu={true} />
              <MenuBottom />
            </Caly_MenuSpalte>
          </Caly_HeroZeile>

          <Caly_HeroUnten>
            <Caly_HeroH1>
              Starte deine
              <br />
              Karriere
              <br />
              bei uns
            </Caly_HeroH1>
            <Caly_HeroDescBox>
              <Caly_Stern>
                <Caly_SternIcon />
              </Caly_Stern>
              <Caly_HeroDesc>
                Bei EXTENDIT legen wir großen Wert auf positiven Teamspirit und
                Professionalität. Wenn du dich persönlich und beruflich
                weiterentwickeln möchtest, findest du bei uns ein ideales
                Umfeld. Wir fördern persönliches Engagement und unser Ziel ist
                es, ein angenehmes Arbeitsumfeld zu schaffen, wo Wachstum und
                gute Stimmung eine Einheit bilden.
              </Caly_HeroDesc>
            </Caly_HeroDescBox>
          </Caly_HeroUnten>

          {/* ── MARQUEE ────────────────────────────────────────── */}
          <Caly_MarqueeWrapper>
            <Caly_MarqueeLauf>
              {[...caly_marqueeBasis, ...caly_marqueeBasis].map(
                function caly_renderMarqueePill({ node: caly_job }, caly_i) {
                  return (
                    <Caly_MarqueePill
                      key={`marq-${caly_i}`}
                      to={`/jobs/${caly_job.slug}/`}
                    >
                      {caly_job.title}
                    </Caly_MarqueePill>
                  )
                }
              )}
            </Caly_MarqueeLauf>
          </Caly_MarqueeWrapper>

          {/* ── SEKTION 01 ─────────────────────────────────────── */}
          <Caly_SektionRow>
            <Caly_SektionNummer>01</Caly_SektionNummer>
            <Caly_SektionTitel>
              Sympathisch
              <br />
              Zentral
              <br />
              Innovativ
            </Caly_SektionTitel>
          </Caly_SektionRow>

          {/* Fotoband – langsam durchlaufend, Inhalt doppelt für Loop */}
          <Caly_FotoBand>
            <Caly_FotoBandLauf>
              {[...caly_bandFotos, ...caly_bandFotos].map(
                function caly_renderBandFoto({ node: caly_foto }, caly_i) {
                  return (
                    <img
                      key={`band-${caly_i}`}
                      src={caly_foto.childImageSharp.fixed.src}
                      srcSet={caly_foto.childImageSharp.fixed.srcSet}
                      alt="Das EXTENDIT Team"
                      loading="lazy"
                    />
                  )
                }
              )}
            </Caly_FotoBandLauf>
          </Caly_FotoBand>

          {/* Textblock unter dem Fotoband */}
          <Caly_TeamTextRow>
            <Caly_TeamTextLinks>
              Kleines Team,
              <br />
              große Chancen
            </Caly_TeamTextLinks>
            <Caly_TeamTextRechts>
              Bei EXTENDIT legen wir großen Wert auf positiven Teamspirit und
              Professionalität. Wenn du dich persönlich und beruflich
              weiterentwickeln möchtest, findest du bei uns ein ideales Umfeld.
              Wir fördern persönliches Engagement und unser Ziel ist es, ein
              angenehmes Arbeitsumfeld zu schaffen, wo Wachstum und gute
              Stimmung eine Einheit bilden.
            </Caly_TeamTextRechts>
          </Caly_TeamTextRow>

          {/* ── WIR SUCHEN AKTUELL ─────────────────────────────── */}
          <Caly_JobsHeader>
            Wir suchen
            <br />
            aktuell:
          </Caly_JobsHeader>
          <Caly_JobsContainer id="caly-jobliste">
            {caly_jobs.map(function caly_renderJobZeile({ node: caly_job }) {
              return (
                <Caly_JobZeile key={caly_job.contentful_id}>
                  <Caly_JobTitel>{caly_job.title}</Caly_JobTitel>
                  <Caly_JobOrt>{caly_job.ort}</Caly_JobOrt>
                  <Caly_JobZeit>{caly_job.arbeitszeit}</Caly_JobZeit>
                  <Caly_JobLink to={`/jobs/${caly_job.slug}/`}>
                    <span>mehr Infos</span>
                  </Caly_JobLink>
                </Caly_JobZeile>
              )
            })}
          </Caly_JobsContainer>

          {/* ── SEKTION 02: KULTUR BEI EXTENDIT ────────────────── */}
          <Caly_SektionRow>
            <Caly_SektionNummer>02</Caly_SektionNummer>
            <Caly_SektionTitel>
              Kultur
              <br />
              bei EXTENDIT
            </Caly_SektionTitel>
          </Caly_SektionRow>

          {/* 6 Kultur-Icons in zwei Spalten */}
          <Caly_KulturGrid>
            {caly_kulturDaten.map(function caly_renderKultur(caly_item) {
              return (
                <Caly_KulturKarte key={caly_item.id}>
                  <Caly_KulturIcon>
                    <img src={caly_item.bild} alt="" loading="lazy" />
                  </Caly_KulturIcon>
                  <Caly_KulturText>{caly_item.text}</Caly_KulturText>
                </Caly_KulturKarte>
              )
            })}
          </Caly_KulturGrid>

          {/* Textblock unter den Icons – Text laut Ticket 20022 */}
          <Caly_KulturTextZwei>
            <Caly_KulturTextSpalte>
              Unser Team ist der Garant für den Erfolg des Unternehmens.
              Deshalb legen wir großen Wert auf die Unternehmenskultur und den
              Wohlfühlfaktor im Team. Entwicklungsmöglichkeit für die
              Mitarbeiter sind uns ein großes Anliegen, ebenso wie die
              Bereitschaft gemeinsam und miteinander zu wachsen.
            </Caly_KulturTextSpalte>
          </Caly_KulturTextZwei>

          {/* Team-Slider (lila Balken, Swiper, Daten aus Contentful) */}
          <Caly_TeamSlider />

          {/* ── SEKTION 03: ANSPRECHPARTNER ────────────────────── */}
          <Caly_SektionRow>
            <Caly_SektionNummer>03</Caly_SektionNummer>
            <Caly_SektionTitel>Ansprech&shy;partner</Caly_SektionTitel>
          </Caly_SektionRow>

          <Caly_KontaktRow>
            {/* Rundes Foto links */}
            {caly_haraldFoto && (
              <Caly_KontaktFotoWrapper>
                <Img
                  fluid={caly_haraldFoto}
                  alt="Harald Pfeiffer"
                  imgStyle={{ objectFit: 'cover', objectPosition: 'center center' }}
                />
              </Caly_KontaktFotoWrapper>
            )}

            <Caly_KontaktInhalt>
              <Caly_KontaktName>Harald Pfeiffer</Caly_KontaktName>
              <Caly_KontaktRolle>Geschäftsführer</Caly_KontaktRolle>

              {/* E-Mail-Button statt Formular (Kundenwunsch):
                  öffnet direkt eine Mail an Harald Pfeiffer */}
              <Caly_MailBtn href="mailto:h.pfeiffer@extendit.at?subject=Bewerbung%20%2F%20Anfrage%20Jobs">
                E-Mail senden
              </Caly_MailBtn>

              {/* Call-Zeile laut Design */}
              <Caly_CallZeile>
                <span className="caly-call">Call</span>
                <a href="tel:+4318027657">+43 – (0)1 – 802 76 57 37</a>
              </Caly_CallZeile>
            </Caly_KontaktInhalt>
          </Caly_KontaktRow>
        </Caly_Seite>
      </Container>
    </Layout>
  )
}

// ─────────────────────────────────────────────────────────────
// 5. GraphQL-Abfrage & Export
// ─────────────────────────────────────────────────────────────

export default caly_JobsSeite
